/**
 * Edit form for an establishment.
 */
import * as React from 'react';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { classFromSchoolClass } from '@/components/forms/ClassesGrid';
import { ESTABLISHMENT_FORM_QUERY_KEYS } from '@/components/establishments/establishment-configs';
import {
  applyEstablishmentFormField,
  applyEstablishmentFormPatch,
  buildUpdateEstablishmentDto,
  currentProspectionLabel,
  emptyEditEstablishmentForm,
  establishmentToFormData,
  formatEstablishmentPersonLabel,
  getClassesError,
  mapEditableProspectionOptions,
  revalidateLegacyContactFields,
} from '@/components/establishments/establishment-form-utils';
import { EstablishmentGeneralSection } from '@/components/establishments/establishment-form-builder/EstablishmentGeneralSection';
import { EstablishmentAddressSection } from '@/components/establishments/establishment-form-builder/EstablishmentAddressSection';
import { EstablishmentContactsSection } from '@/components/establishments/establishment-form-builder/EstablishmentContactsSection';
import { EstablishmentEngagementSection } from '@/components/establishments/establishment-form-builder/EstablishmentEngagementSection';
import { EstablishmentProspectionSection } from '@/components/establishments/establishment-form-builder/EstablishmentProspectionSection';
import { EstablishmentPartnershipSection } from '@/components/establishments/establishment-form-builder/EstablishmentPartnershipSection';
import { EstablishmentManagersSection } from '@/components/establishments/establishment-form-builder/EstablishmentManagersSection';
import { EstablishmentClassroomsSection } from '@/components/establishments/establishment-form-builder/EstablishmentClassroomsSection';
import { EstablishmentClassesSection } from '@/components/establishments/establishment-form-builder/EstablishmentClassesSection';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getEstablishment,
  updateEstablishment,
  getClassrooms,
  getSchoolClasses,
} from '@/services/establishments.service';
import type { EstablishmentFormState, UpdateEstablishmentDto } from '@/types/establishment.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { establishmentValidationSchema } from '@/utils/validation-schemas-utils';

/** Props for {@link EditEstablishmentView}. */
interface EditEstablishmentViewProps {
  establishmentId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for an establishment. */
export const EditEstablishmentView: React.FC<EditEstablishmentViewProps> = ({
  establishmentId,
  onClose,
  onSuccess,
}) => {
  const { user, isManager } = useConnectedUser();
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState(emptyEditEstablishmentForm);
  const [isHydrated, setIsHydrated] = React.useState(false);
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  const validation = useFormValidation(establishmentValidationSchema);

  const {
    options: coordinatorOptions,
    isLoading: coordinatorLoading,
    onSearch: onCoordinatorSearch,
  } = useAdminAutocomplete({
    roleCodes: ['coordinator'],
    managedById: formData.managedById || undefined,
    enabled: !!formData.managedById,
  });

  const {
    options: managerOptions,
    isLoading: managerLoading,
    onSearch: onManagerSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager'],
  });

  const {
    data: establishment,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ESTABLISHMENT_FORM_QUERY_KEYS.one(establishmentId),
    queryFn: () => getEstablishment(establishmentId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'establishmentNotFound',
    onClose,
    entityId: establishmentId,
  });

  const editableProspectionOptions = useMemo(
    () => mapEditableProspectionOptions(establishment?.editableProspectionStatuses),
    [establishment?.editableProspectionStatuses],
  );

  const { data: existingClassroomsRaw } = useQuery({
    queryKey: ESTABLISHMENT_FORM_QUERY_KEYS.classrooms(establishmentId),
    queryFn: () => getClassrooms(establishmentId),
    enabled: !!establishmentId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const existingClassrooms = React.useMemo(
    () => existingClassroomsRaw ?? [],
    [existingClassroomsRaw],
  );

  const { data: existingClassesRes } = useQuery({
    queryKey: ESTABLISHMENT_FORM_QUERY_KEYS.schoolClasses(establishmentId),
    queryFn: () => getSchoolClasses({ establishmentId, pageSize: 20 }),
    enabled: !!establishmentId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const existingClasses = React.useMemo(() => existingClassesRes?.data ?? [], [existingClassesRes]);

  React.useEffect(() => {
    if (establishment) {
      const initialData = establishmentToFormData(
        establishment,
        existingClassrooms,
        existingClasses.map(classFromSchoolClass),
      );
      formDataRef.current = initialData;
      setFormData(initialData);
      setIsHydrated(true);
      validation.validateInitialFormData(initialData);
    }
  }, [establishment, existingClassrooms, existingClasses]);

  const updateMutation = useMutation({
    mutationFn: async (dto: UpdateEstablishmentDto) => {
      await updateEstablishment(establishmentId, dto);
      await queryClient.invalidateQueries({
        queryKey: ESTABLISHMENT_FORM_QUERY_KEYS.schoolClasses(establishmentId),
      });
    },
    onSuccess: async () => {
      toast.success('Établissement modifié avec succès !');
      await queryClient.invalidateQueries({ queryKey: ESTABLISHMENT_FORM_QUERY_KEYS.list });
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleFieldChange = (field: string, value: unknown) => {
    const updatedData = applyEstablishmentFormField(formDataRef.current, field, value);
    formDataRef.current = updatedData;
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    for (const legacyField of revalidateLegacyContactFields(field, value)) {
      validation.validateField(legacyField, updatedData[legacyField], updatedData);
    }
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleFormPatch = (patch: Partial<EstablishmentFormState>) => {
    const updatedData = applyEstablishmentFormPatch(formDataRef.current, patch);
    formDataRef.current = updatedData;
    setFormData(updatedData);
    for (const [key, value] of Object.entries(patch)) {
      validation.validateField(key, value, updatedData);
      validation.markAsTouched(key);
      validation.markAsDirty(key);
    }
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    updateMutation.mutate(
      buildUpdateEstablishmentDto(formData, { isManager, currentUserId: user?.id }),
    );
  };

  const classesError = useMemo(
    () => getClassesError(formData.type, formData.classes),
    [formData.classes, formData.type],
  );

  const fieldValidation = {
    getFieldState: validation.getFieldState,
    getFieldError: validation.getFieldError,
  };

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier l'établissement"
      subtitle="Modifiez les informations de l'établissement"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid() && !classesError}
      isLoading={isLoading || (!!establishment && !isHydrated)}
    >
      <div className="space-y-6">
        <EstablishmentGeneralSection
          mode="edit"
          form={formData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
        />
        <EstablishmentAddressSection
          form={formData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          onFormPatch={handleFormPatch}
          validation={fieldValidation}
        />
        <EstablishmentContactsSection form={formData} onFieldChange={handleFieldChange} />
        <EstablishmentEngagementSection
          mode="edit"
          form={formData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
        />
        <EstablishmentProspectionSection
          mode="edit"
          form={formData}
          onFieldChange={handleFieldChange}
          currentProspectionLabel={currentProspectionLabel(establishment?.prospectionStatus)}
          editableProspectionOptions={editableProspectionOptions}
        />
        <EstablishmentPartnershipSection form={formData} onFieldChange={handleFieldChange} />
        <EstablishmentManagersSection
          form={formData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={fieldValidation}
          isManager={isManager}
          managerLabel={formatEstablishmentPersonLabel(user ?? {})}
          managerOptions={managerOptions}
          managerLoading={managerLoading}
          onManagerSearch={onManagerSearch}
          coordinatorOptions={coordinatorOptions}
          coordinatorLoading={coordinatorLoading}
          onCoordinatorSearch={onCoordinatorSearch}
        />
        <EstablishmentClassroomsSection form={formData} onFieldChange={handleFieldChange} />
        <EstablishmentClassesSection
          mode="edit"
          form={formData}
          onFieldChange={handleFieldChange}
          classesError={classesError}
        />
      </div>
    </CollapseReplaceForm>
  );
};
